import React from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import InfoText from '@/components/atoms/InfoText';
import { cn } from '@/lib/helperClass';

export interface LogUser {
  name?: string;
  role?: string;
}

export interface IndividualLogItemProps {
  title: string;
  msg?: string;
  message?: string;
  date: string | Date;
  user?: LogUser;
  className?: string;
}

export const IndividualLogItem: React.FC<IndividualLogItemProps> = ({
  title,
  msg,
  date,
  user,
  className,
}) => {
  const { t, i18n } = useTranslation();

  const formattedDate = React.useMemo(() => {
    if (!date) return '-';
    const parsed = dayjs(date);
    if (!parsed.isValid()) return String(date);
    return parsed.locale(i18n.language).format('MMMM DD, YYYY, hh:mm a');
  }, [date, i18n.language]);

  return (
    <div
      className={cn('flex items-center gap-3 w-full', className)}
      data-testid="individual-log-item-container"
    >
      {/* Icon */}
      <div data-testid="individual-log-item-marker-col">
        <div
          className="w-4 h-4 rounded-full border border-light-text-primary dark:border-dark-text-primary flex items-center justify-center bg-light-surface-primary dark:bg-dark-surface-primary shrink-0 mt-0.5"
          data-testid="individual-log-item-marker-outer"
        >
          <div
            className="w-2 h-2 rounded-full bg-light-text-primary dark:bg-dark-text-primary"
            data-testid="individual-log-item-marker-inner"
          />
        </div>
      </div>

      <div
        className="flex flex-col gap-1 flex-1"
        data-testid="individual-log-item-content-col"
      >
        <div
          className="flex items-center justify-between gap-4 w-full"
          data-testid="individual-log-item-header-row"
        >
          <div
            className="flex items-center gap-1 min-w-0"
            data-testid="individual-log-item-title-wrapper"
          >
            <InfoText
              text={`${t('activityLogs.actionPrefix')} ${title}`}
              className="!text-base !font-bold capitalize text-light-text-primary dark:text-dark-text-primary"
              data-testid="individual-log-item-title-text"
            />
          </div>

          <div
            className="shrink-0 text-end"
            data-testid="individual-log-item-date-wrapper"
          >
            <InfoText
              text={formattedDate}
              className="!text-xs text-light-text-secondary dark:text-dark-text-secondary"
              data-testid="individual-log-item-date-text"
            />
          </div>
        </div>

        {user?.name && (
          <div
            className="flex items-center gap-1 w-full"
            data-testid="individual-log-item-user-wrapper"
          >
            <InfoText
              text={user.role ? `${user.name} (${user.role})` : user.name}
              className="!text-sm dark:text-dark-text-secondary"
              data-testid="individual-log-item-user-text"
            />
          </div>
        )}

        <div
          className="flex items-start gap-1 w-full"
          data-testid="individual-log-item-msg-wrapper"
        >
          <InfoText
            text={t('activityLogs.messagePrefix')}
            className="!text-sm !font-semibold text-light-text-primary dark:text-dark-text-primary shrink-0"
            data-testid="individual-log-item-msg-prefix"
          />

          <InfoText
            text={msg?.toString() as string}
            className="!text-sm font-normal text-light-text-primary dark:text-dark-text-primary break-words flex-1 min-w-0"
            data-testid="individual-log-item-msg-text"
          />
        </div>
      </div>
    </div>
  );
};

export default IndividualLogItem;
